// NG core
import { BrowserModule } from '@angular/platform-browser';
import {APP_INITIALIZER, ErrorHandler, Injector, NgModule} from '@angular/core';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import { TranslateModule } from '@ngx-translate/core';
import { AppRoutingModule } from './app.routes';
import { SimpleNotificationsModule } from 'angular2-notifications';
import { TabsModule } from 'ngx-bootstrap/tabs';
import { BsDatepickerModule } from 'ngx-bootstrap/datepicker';
import { ModalModule } from 'ngx-bootstrap/modal';
import { NgxDatatableModule } from '@swimlane/ngx-datatable';
import { CONSTANTS, ConstantsModule } from './config/constants.module';
import { ConfigService } from './config/config.service';
import { AppComponent } from './app.component';
import { CoreModule } from './core/core.module';
import { ComponentModule } from './component/component.module';
import { SharedModule } from './shared/shared.module';
import { PageModule } from './page/page.module';
import { EsriModule } from './config/esri.module';
import { UserPortalService } from './core/service/user-portal.service';
import { UserService } from './core/service/user.service';
import { PortalAuthModule, PortalAuthService } from 'portal-auth';
import { ENVIRONMENT } from '../environments/environment';
import { SbbPrintModule } from '@sbb-amsgis/sbb-print';
import { APP_CONFIG } from '../assets/config/shared/app.config';
import { AppInjector } from './core/service/app-injector.service';
import { ServiceWorkerModule } from '@angular/service-worker';
import {SBB_LANGUAGE_SERVICE, SBB_QUERY_SERVICE, SbbInfoBannerModule, SbbQueryService} from '@sbb-amsgis/sbb-info-banner';
import {LanguageService} from './shared/service/language.service';
import {EsriService} from './core/service/esri.service';
import {ErrorHandlingService} from './core/service/error-handling.service';


export function ConfigLoaderFactory(configService: ConfigService): Function {
  return () => configService.loadAllConfigs();
}

export function userServiceFactory(
  configService: ConfigService,
  CONST: any,
  portalAuthService: PortalAuthService
): UserService {
  return new UserPortalService(configService, CONST, portalAuthService);
}

@NgModule({
  declarations: [AppComponent],
  bootstrap: [AppComponent],
  imports: [
    AppRoutingModule,
    TranslateModule.forRoot(),
    SimpleNotificationsModule.forRoot(),
    TabsModule.forRoot(),
    BsDatepickerModule.forRoot(),
    ModalModule.forRoot(),
    BrowserModule,
    BrowserAnimationsModule,
    CoreModule,
    ConstantsModule,
    EsriModule,
    NgxDatatableModule,
    ComponentModule,
    SharedModule,
    PageModule,
    PortalAuthModule.forRoot({
      clientId: ENVIRONMENT.arcgisOauthConfig.clientId,
      portalUrl: ENVIRONMENT.arcgisOauthConfig.portalUrl,
      portalClientId: 'arcgisonline',
    }),
    SbbPrintModule.forRoot({
      printServiceUrl: ENVIRONMENT.baseUrl + APP_CONFIG.print.printServiceUrl,
      layoutInfo: APP_CONFIG.print.layoutInfo,
      defaultScale: 1000,
    }),
    ServiceWorkerModule.register('ngsw-worker.js', {
      enabled: ENVIRONMENT.pwaEnabled,
      // Register the ServiceWorker as soon as the application is stable
      // or after 30 seconds (whichever comes first).
      registrationStrategy: 'registerWhenStable:30000',
    }),
    SbbInfoBannerModule.forRoot(ENVIRONMENT.sbbInfoBannerConfig),
  ],
  providers: [
    { provide: ErrorHandler, useClass: ErrorHandlingService },
    {
      provide: APP_INITIALIZER,
      useFactory: ConfigLoaderFactory,
      deps: [ConfigService],
      multi: true,
    },
    {
      provide: UserService,
      useFactory: userServiceFactory,
      deps: [ConfigService, CONSTANTS, PortalAuthService],
    },
    {
      provide: SBB_LANGUAGE_SERVICE,
      useClass: LanguageService,
    },
    {
      provide: SBB_QUERY_SERVICE,
      useClass: SbbQueryService, // could use the default SbbQueryService from the library
      deps: [EsriService], // Need the EsriService to be initialized for the case transformation
    },
    provideHttpClient(withInterceptorsFromDi()),
  ],
})
export class AppModule {
  constructor(injector: Injector) {
    AppInjector.setInjector(injector);
  }
}
